// Base Camp exterior: grass, flowers, the house with its sign. She walks to a door to go in.

import { h, clear } from '@kit/dom';
import { play as playSound } from '@kit/audio';
import { openRegions, regionQuestsForToday } from '../regions';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import { drawSprite } from '@avatar/render';
import { getSave, subscribe, update as updateSave } from '../state';
import { go } from '../router';
import { topBar } from '../ui';
import { activePet, drawPet } from '../pets';
import { drawArt, hasArt, onArtReady } from '@kit/assets';
import { houseStories } from '../house';

interface Spot { id: string; x: number; y: number; label: string; to: string }

export function hubScreen(root: HTMLElement) {
  if (!getSave().onboarded) { go('studio'); return; }

  const bar = topBar('Base Camp', { longPress: () => go('parent') });
  const stage = h('div', { style: { padding: '0 12px' } });
  const hint = h('div.msg', null, 'Tap a place to walk there.');
  const buttons = h('div.grid2.pad', null,
    h('button.btn', { onclick: () => walkTo('academy') }, 'Academy'),
    h('button.btn', { onclick: () => walkTo('house') }, 'My House'),
    h('button.btn', { onclick: () => walkTo('wilds') }, 'The Wilds'),
    h('button.btn', { onclick: () => walkTo('shop') }, 'Shop'),
    h('button.btn', { onclick: () => walkTo('town') }, 'Town'),
    h('button.btn', { onclick: () => go('map') }, 'World Map'),
  );
  // signposts for every region she has opened, with today's quest status
  const regionRow = h('div', { style: { padding: '0 16px 16px' } });
  function renderRegions() {
    clear(regionRow);
    const open = openRegions();
    if (!open.length) return;
    const quests = regionQuestsForToday();
    regionRow.append(h('p.muted.small', { style: { margin: '0 0 6px' } }, 'Places you have opened'));
    regionRow.append(h('div.grid2', null, open.map(r => {
      const q = quests.find(x => x.key.startsWith(`region.${r.id}:`));
      return h('button.btn', { onclick: () => go(`region/${r.id}`), style: { borderColor: r.color, borderWidth: '3px', flexDirection: 'column', gap: '0', lineHeight: '1.15' } },
        h('span', null, r.name),
        h('span.small', { style: { fontWeight: '700', color: q && !q.done ? r.color : 'var(--text-2)' } }, q ? (q.done ? 'Quest done' : 'New quest!') : ''));
    })));
  }
  renderRegions();
  root.append(bar, stage, hint, buttons, regionRow);

  // a region that opened since she last looked gets a celebration
  const fresh = openRegions().filter(r => !getSave().regionsSeen.includes(r.id));
  if (fresh.length) {
    const r = fresh[0];
    const overlay = h('div.overlay', null, h('div.sheet.center', null,
      h('h2', null, `${r.name} is open!`),
      h('p', null, `${r.guide.name}, ${r.guide.role}, has a quest for you every day.`),
      h('div.grid2', null,
        h('button.btn', { onclick: () => { seen(); overlay.remove(); } }, 'Later'),
        h('button.btn.primary', { onclick: () => { seen(); overlay.remove(); go(`region/${r.id}`); } }, 'Go there'),
      ),
    ));
    const seen = () => updateSave(s => { for (const f of fresh) if (!s.regionsSeen.includes(f.id)) s.regionsSeen.push(f.id); });
    root.append(overlay);
    playSound('chime');
  }

  let spots: Spot[] = [];
  let px = 0.5, py = hasArt('scene.hub') ? 0.9 : 0.86;   // avatar position (fractions of width / height)
  let tx = px, ty = py;
  let dest: Spot | null = null;
  let walkT = 0;
  let facing: 'down' | 'up' | 'left' | 'right' = 'down';
  let t = 0;

  // The painted scene is square; the procedural one was laid out at 1.05. Square works for both.
  const S = makeSurface(stage, { aspect: 1.0, draw: false, onResize: layout });

  function layout() {
    spots = hasArt('scene.hub')
      ? [ // positions of the landmarks in the painted scene (scene.hub, candidate 4)
        { id: 'academy', x: 0.12, y: 0.76, label: 'Academy', to: 'academy' },
        { id: 'house', x: 0.50, y: 0.66, label: 'My House', to: 'house' },
        { id: 'wilds', x: 0.86, y: 0.60, label: 'The Wilds', to: 'wilds' },
        { id: 'shop', x: 0.76, y: 0.90, label: 'Shop', to: 'shop' },
        { id: 'town', x: 0.10, y: 0.92, label: 'Town', to: 'town' },
      ]
      : [
        { id: 'academy', x: 0.16, y: 0.70, label: 'Academy', to: 'academy' },
        { id: 'house', x: 0.50, y: 0.62, label: 'My House', to: 'house' },
        { id: 'wilds', x: 0.85, y: 0.70, label: 'The Wilds', to: 'wilds' },
        { id: 'shop', x: 0.82, y: 0.92, label: 'Shop', to: 'shop' },
        { id: 'town', x: 0.14, y: 0.93, label: 'Town', to: 'town' },
      ];
  }
  const unArt = onArtReady(() => { layout(); if (!dest) { px = 0.5; py = hasArt('scene.hub') ? 0.9 : 0.86; tx = px; ty = py; } });

  function walkTo(id: string) {
    const s = spots.find(x => x.id === id);
    if (!s) return;
    dest = s; tx = s.x; ty = s.y;
    hint.textContent = `Walking to the ${s.label}...`;
  }

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      const W = S.w, H = S.h;
      // avatar tap -> studio
      if (Math.abs(p.x - px * W) < 28 && p.y > py * H - 70 && p.y < py * H + 10) { go('studio'); return; }
      let best: Spot | null = null, bd = 1e9;
      for (const s of spots) {
        const d = Math.hypot(p.x - s.x * W, p.y - s.y * H);
        if (d < bd) { bd = d; best = s; }
      }
      if (best && bd < Math.max(70, W * 0.18)) walkTo(best.id);
    },
  });

  function update(dt: number) {
    t += dt;
    const dx = tx - px, dy = ty - py;
    const d = Math.hypot(dx, dy);
    if (d > 0.004) {
      const sp = 0.45 * dt;
      const k = Math.min(1, sp / d);
      px += dx * k; py += dy * k;
      walkT += dt * 8;
      facing = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
    } else if (dest) {
      const to = dest.to; dest = null;
      setTimeout(() => go(to), 120);
    } else {
      walkT = 0;
    }
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    const save = getSave();
    if (drawArt(c, 'scene.hub', 0, 0, W, H)) {
      // painted scene: the sign letters, place labels and the moving things go on top
      c.fillStyle = '#f5c542'; c.font = `900 ${Math.round(H * 0.045)}px "Baloo 2", Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText(save.settings.sign || 'AR', W * 0.5, H * 0.333);
      for (const s of spots) {
        if (s.id === 'house') continue;
        const fs = Math.round(Math.max(12, W * 0.034));
        c.font = `800 ${fs}px "Baloo 2", Nunito, sans-serif`;
        const tw = c.measureText(s.label).width + 16;
        const lx = Math.min(W - tw / 2 - 4, Math.max(tw / 2 + 4, s.x * W)), ly = s.y * H - (s.id === 'wilds' ? 0 : fs * 1.6);
        c.fillStyle = 'rgba(247,243,234,.92)'; roundRect(c, lx - tw / 2, ly - fs * 0.75, tw, fs * 1.5, fs * 0.75); c.fill();
        c.strokeStyle = '#3b2a1e'; c.lineWidth = 1.5; c.stroke();
        c.fillStyle = '#3b2a1e'; c.textBaseline = 'middle'; c.fillText(s.label, lx, ly + 1);
      }
      drawActors(c, W, H, save);
      return;
    }
    // sky
    c.fillStyle = '#bfe3f5'; c.fillRect(0, 0, W, H);
    // sun
    c.fillStyle = '#f5c542'; c.beginPath(); c.arc(W * 0.86, H * 0.12, W * 0.06, 0, 7); c.fill();
    // hills
    c.fillStyle = '#8fd08a'; c.beginPath(); c.ellipse(W * 0.2, H * 0.5, W * 0.5, H * 0.16, 0, 0, 7); c.fill();
    c.fillStyle = '#7cc47a'; c.beginPath(); c.ellipse(W * 0.85, H * 0.52, W * 0.45, H * 0.14, 0, 0, 7); c.fill();
    // grass
    c.fillStyle = '#9ad67e'; c.fillRect(0, H * 0.52, W, H * 0.48);
    // path
    c.fillStyle = '#e4cfa0'; c.beginPath(); c.moveTo(W * 0.42, H * 0.70); c.lineTo(W * 0.58, H * 0.70); c.lineTo(W * 0.66, H); c.lineTo(W * 0.34, H); c.closePath(); c.fill();
    // flowers
    const flowers = [[0.06, 0.62, '#ee86b7'], [0.12, 0.80, '#f5c542'], [0.28, 0.92, '#e2574c'], [0.70, 0.60, '#f6f3ec'], [0.93, 0.80, '#ee86b7'], [0.60, 0.95, '#f5c542'], [0.22, 0.58, '#f6f3ec'], [0.75, 0.86, '#9b6be6']] as const;
    for (const [fx, fy, col] of flowers) {
      const x = fx * W, y = fy * H;
      c.strokeStyle = '#4f9a4a'; c.lineWidth = 2; c.beginPath(); c.moveTo(x, y); c.lineTo(x, y - 10); c.stroke();
      c.fillStyle = col as string; for (let i = 0; i < 5; i++) { c.beginPath(); c.arc(x + Math.cos(i * 1.257) * 4, y - 10 + Math.sin(i * 1.257) * 4, 3, 0, 7); c.fill(); }
      c.fillStyle = '#f5c542'; c.beginPath(); c.arc(x, y - 10, 2, 0, 7); c.fill();
    }
    // house (a second story rises above the ground floor once she has built it)
    const hw = W * 0.40, hh = H * 0.28, hx = W * 0.5 - hw / 2, hy = H * 0.36;
    const extra = houseStories(save) === 2 ? hh * 0.5 : 0;
    c.fillStyle = '#f6efdc'; c.fillRect(hx, hy - extra, hw, hh + extra);
    if (extra) {
      c.fillStyle = '#e4cfa0'; c.fillRect(hx, hy - 3, hw, 5);
      c.fillStyle = '#bfe8ff'; c.fillRect(hx + hw * 0.14, hy - extra + hh * 0.12, hw * 0.18, hh * 0.25); c.fillRect(hx + hw * 0.68, hy - extra + hh * 0.12, hw * 0.18, hh * 0.25);
      c.strokeStyle = '#f6f3ec'; c.lineWidth = 2; c.strokeRect(hx + hw * 0.14, hy - extra + hh * 0.12, hw * 0.18, hh * 0.25); c.strokeRect(hx + hw * 0.68, hy - extra + hh * 0.12, hw * 0.18, hh * 0.25);
    }
    c.fillStyle = '#e2574c'; c.beginPath(); c.moveTo(hx - hw * 0.1, hy - extra); c.lineTo(W * 0.5, hy - extra - hh * 0.55); c.lineTo(hx + hw * 1.1, hy - extra); c.closePath(); c.fill();
    // chimney
    c.fillStyle = '#8a5a3b'; c.fillRect(hx + hw * 0.72, hy - extra - hh * 0.45, hw * 0.1, hh * 0.3);
    // door
    const dw = hw * 0.22, dh = hh * 0.5;
    c.fillStyle = '#8a5a3b'; roundRect(c, W * 0.5 - dw / 2, hy + hh - dh, dw, dh, 6); c.fill();
    c.fillStyle = '#f5c542'; c.beginPath(); c.arc(W * 0.5 + dw * 0.3, hy + hh - dh * 0.5, 3, 0, 7); c.fill();
    // windows
    c.fillStyle = '#bfe8ff';
    c.fillRect(hx + hw * 0.1, hy + hh * 0.2, hw * 0.18, hh * 0.25);
    c.fillRect(hx + hw * 0.72, hy + hh * 0.2, hw * 0.18, hh * 0.25);
    c.strokeStyle = '#f6f3ec'; c.lineWidth = 2;
    c.strokeRect(hx + hw * 0.1, hy + hh * 0.2, hw * 0.18, hh * 0.25);
    c.strokeRect(hx + hw * 0.72, hy + hh * 0.2, hw * 0.18, hh * 0.25);
    // sign
    c.fillStyle = '#2a2a2e'; roundRect(c, W * 0.5 - hw * 0.14, hy + hh * 0.08, hw * 0.28, hh * 0.22, 4); c.fill();
    c.fillStyle = '#f5c542'; c.font = `900 ${Math.round(hh * 0.16)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
    c.fillText(save.settings.sign || 'AR', W * 0.5, hy + hh * 0.19);
    // academy signpost (left)
    signpost(c, W * 0.16, H * 0.70, '#4f8fe6', 'Academy', W);
    bell(c, W * 0.16, H * 0.56, W);
    // wilds (right): trees
    tree(c, W * 0.80, H * 0.66, W * 0.06, H * 0.14);
    tree(c, W * 0.90, H * 0.68, W * 0.05, H * 0.12);
    signpost(c, W * 0.85, H * 0.78, '#f08a3c', 'The Wilds', W);
    // shop cart
    cart(c, W * 0.82, H * 0.92, W);
    // road to town (bottom left) with a sign
    c.fillStyle = '#e4cfa0'; c.beginPath(); c.moveTo(W * 0.34, H); c.lineTo(W * 0.40, H * 0.9); c.lineTo(W * 0.0, H * 0.84); c.lineTo(W * 0.0, H); c.closePath(); c.fill();
    signpost(c, W * 0.14, H * 0.84, '#5fbf6a', 'Town', W);
    drawActors(c, W, H, save);
  }

  function drawActors(c: CanvasRenderingContext2D, W: number, H: number, save: ReturnType<typeof getSave>) {
    // avatar shadow + sprite, pet beside
    const ax = px * W, ay = py * H;
    c.fillStyle = 'rgba(0,0,0,.15)'; c.beginPath(); c.ellipse(ax, ay + 2, 12, 5, 0, 0, 7); c.fill();
    const frame = walkT > 0 ? (Math.floor(walkT) % 4) : 0;
    const sc = Math.max(2.2, W / 170);
    drawSprite(c, save.avatar, ax, ay, sc, frame, facing);
    const pet = activePet(save);
    if (pet) drawPet(c, pet, ax + (facing === 'left' ? 1 : -1) * sc * 11, ay + 2, sc, frame);
  }

  function signpost(c: CanvasRenderingContext2D, x: number, y: number, col: string, text: string, W: number) {
    c.fillStyle = '#8a5a3b'; c.fillRect(x - 3, y - 36, 6, 40);
    c.fillStyle = col; roundRect(c, x - W * 0.12, y - 40, W * 0.24, 22, 5); c.fill();
    c.fillStyle = '#fff'; c.font = `800 ${Math.round(W * 0.034)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
    c.fillText(text, x, y - 29);
  }
  function bell(c: CanvasRenderingContext2D, x: number, y: number, W: number) {
    c.fillStyle = '#f5c542'; c.beginPath(); c.arc(x, y, W * 0.03, Math.PI, 0); c.lineTo(x + W * 0.035, y + W * 0.02); c.lineTo(x - W * 0.035, y + W * 0.02); c.closePath(); c.fill();
    c.fillStyle = '#b77700'; c.beginPath(); c.arc(x, y + W * 0.025, 3, 0, 7); c.fill();
  }
  function tree(c: CanvasRenderingContext2D, x: number, y: number, r: number, hgt: number) {
    c.fillStyle = '#8a5a3b'; c.fillRect(x - r * 0.18, y - hgt * 0.3, r * 0.36, hgt * 0.45);
    c.fillStyle = '#4f9a4a';
    c.beginPath(); c.moveTo(x - r, y - hgt * 0.25); c.lineTo(x, y - hgt); c.lineTo(x + r, y - hgt * 0.25); c.closePath(); c.fill();
    c.beginPath(); c.moveTo(x - r * 0.8, y - hgt * 0.55); c.lineTo(x, y - hgt * 1.2); c.lineTo(x + r * 0.8, y - hgt * 0.55); c.closePath(); c.fill();
  }
  function cart(c: CanvasRenderingContext2D, x: number, y: number, W: number) {
    const w = W * 0.16, hh = W * 0.08;
    c.fillStyle = '#e2574c'; roundRect(c, x - w / 2, y - hh - 10, w, hh, 4); c.fill();
    c.fillStyle = '#f6f3ec'; for (let i = 0; i < 4; i++) c.fillRect(x - w / 2 + i * (w / 4), y - hh - 10, w / 8, hh);
    c.fillStyle = '#8a5a3b'; c.fillRect(x - w / 2, y - 12, w, 6);
    c.fillStyle = '#2a2a2e'; c.beginPath(); c.arc(x - w * 0.3, y - 2, 5, 0, 7); c.arc(x + w * 0.3, y - 2, 5, 0, 7); c.fill();
    c.fillStyle = '#2a2a2e'; c.font = `800 ${Math.round(W * 0.03)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'alphabetic';
    c.fillText('Shop', x, y - hh - 14);
  }

  const loop = makeLoop(update, draw);
  loop.start();
  const un = subscribe(() => { /* avatar or sign may change */ });

  return () => { loop.stop(); unPointer(); S.destroy(); un(); unArt(); };
}
